import _get from 'lodash/get'
import _map from 'lodash/map'
import _each from 'lodash/each'
import _some from 'lodash/some'
import _capitalize from 'lodash/capitalize'
import React, { useState } from 'react'
import Modal from 'react-modal'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

import Button from '../../_library/Button'
import modalStyle from '../../../_common/core/modalStyle'

import * as fb from '../../../_common/core/fb'
import SortableTable from '../../_library/SortableTable'
import { getTableColumns } from '../../utils/sortableTableUtils'

const FaceBookPixels = props => {
  const { values, updateField, submitForm } = props

  const [fbAdAccountsLoading, setFbAdAccountsLoading] = useState(false)
  const [fbAdAccounts, setFbAdAccounts] = useState([])
  const [fbAdAccountsError, setFbAdAccountsError] = useState(null)
  const [showImportFbPixelDialog, setShowImportFbPixelDialog] = useState(false)

  const clickImportFbPixelDialog = () => {
    openImportFbPixelDialog()
    fetchFbPixelsAll()
  }

  const openImportFbPixelDialog = () => {
    setShowImportFbPixelDialog(true)
  }

  const closeImportFbPixelDialog = () => {
    setShowImportFbPixelDialog(false)
    setFbAdAccounts([])
  }

  const fetchFbPixelsAll = async () => {
    setFbAdAccountsLoading(true)

    fb.fetchSDK()
      .catch()
      .then(FB => {
        FB.login(
          () => {
            fetchFbAdAccounts(FB)
          },
          { scope: 'ads_management,business_management' },
        )
      })
  }

  const fetchFbAdAccounts = async (FB, after = null) => {
    let url = '/me/adaccounts?fields=id,name,currency,owner,business'
    if (after) {
      url += '&after=' + after
    }
    FB.api(url, response => {
      let afterRef
      if (_get(response, 'paging.next')) {
        afterRef = _get(response, 'paging.cursors.after')
      }
      const offset = 0

      if (response.data) {
        const fbAdAccounts = _map(response.data, fbAdAccount => ({
          id: fbAdAccount.id,
          name: fbAdAccount.name,
          currency: fbAdAccount.currency,
          owner_id: fbAdAccount.owner,
          business: fbAdAccount.business,
          pixels: [],
          pixelsLoading: true,
          pixelsLoadingError: null,
          pixelCreating: false,
          pixelCreatingError: null,
          owner: fbAdAccount.business ?? null,
        }))

        setFbAdAccounts(currValue => [...currValue, ...fbAdAccounts])
        setFbAdAccountsLoading(false)
        setFbAdAccountsError(null)

        _each(fbAdAccounts, (fbAdAccount, idx) => {
          fetchFbAdAccountOwner(FB, fbAdAccount, offset + idx)
          fetchFbPixels(FB, fbAdAccount, offset + idx)
        })
      } else if (response.error) {
        setFbAdAccountsLoading(false)
        setFbAdAccountsError(response.error.message)
      }

      if (afterRef) {
        fetchFbAdAccounts(FB, afterRef)
      }
    })
  }

  const fetchFbAdAccountOwner = async (FB, fbAdAccount, idx) => {
    if (!fbAdAccount.owner) {
      FB.api('/' + fbAdAccount.owner_id, response => {
        setFbAdAccounts(currValue => {
          const newFbAdAccounts = [...currValue]
          newFbAdAccounts[idx].owner = response

          return newFbAdAccounts
        })
      })
    }
  }

  const fetchFbPixels = async (FB, fbAdAccount, idx, after = null) => {
    setFbAdAccounts(currValue => {
      const newFbAdAccounts = [...currValue]
      newFbAdAccounts[idx].pixelsLoading = true

      return newFbAdAccounts
    })

    const { pixels = [] } = values
    let afterRef = null
    let url = '/' + fbAdAccount.id + '/adspixels?fields=id,name,code'
    if (after) {
      url += '&after=' + after
    }
    FB.api(url, response => {
      if (_get(response, 'paging.next')) {
        afterRef = _get(response, 'paging.cursors.after')
      }
      if (response.data) {
        setFbAdAccounts(currValue => {
          const newFbAdAccounts = [...currValue]
          const accountPixels = (newFbAdAccounts[idx].pixels ?? []).concat(
            _map(response.data, p => ({
              id: p.id,
              name: p.name,
              code: p.code,
              checked: !!pixels.find(pixel => pixel.pixelId === p.id),
              accessToken: pixels.find(pixel => pixel.pixelId === p.id)?.accessToken ?? '',
              error: '',
            })),
          )

          newFbAdAccounts[idx].pixels = accountPixels
          newFbAdAccounts[idx].pixelsLoading = false
          newFbAdAccounts[idx].pixelsLoadingError = null

          return newFbAdAccounts
        })
      } else if (response.error) {
        setFbAdAccounts(currValue => {
          const newFbAdAccounts = [...currValue]
          newFbAdAccounts[idx].pixelsLoading = false
          fbAdAccount.pixelsLoadingError = response.error.message

          return newFbAdAccounts
        })
      }

      if (afterRef) {
        fetchFbPixels(FB, fbAdAccount, idx, afterRef)
      }
    })
  }

  const createFbPixel = (fbAdAccount, idx) => {
    if (fbAdAccount.pixels.length > 0) return
    setFbAdAccounts(currValue => {
      const newFbAdAccounts = [...currValue]
      newFbAdAccounts[idx].pixelCreating = true

      return newFbAdAccounts
    })

    fb.fetchSDK()
      .catch()
      .then(FB => {
        FB.login(
          () => {
            FB.api('/' + fbAdAccount.id + '/adspixels', 'post', { name: 'The Ticket Fairy' }, response => {
              if (response.error) {
                setFbAdAccounts(currValue => {
                  const newFbAdAccounts = [...currValue]
                  newFbAdAccounts[idx].pixelCreating = false
                  newFbAdAccounts[idx].pixelCreatingError = response.error.message

                  return newFbAdAccounts
                })
              } else {
                setFbAdAccounts(currValue => {
                  const newFbAdAccounts = [...currValue]
                  newFbAdAccounts[idx].pixelCreating = false
                  newFbAdAccounts[idx].pixelCreatingError = null

                  return newFbAdAccounts
                })

                fetchFbPixels(FB, fbAdAccount)
              }
            })
          },
          { scope: 'ads_management' },
        )
      })
  }

  const importFacebookPixels = () => {
    const { pixels: savedPixels } = values

    const pixels = fbAdAccounts
      .reduce((curr, account) => {
        const checkedPixels = account.pixels.map(({ id, accessToken, checked }) => {
          const isSaved = savedPixels.find(savedPixel => savedPixel.pixelId === id)
          return !isSaved && !checked
            ? null
            : {
                ...(isSaved ? { id: isSaved.id } : {}),
                pixelId: id,
                accessToken,
                platform: 'facebook',
                ...(isSaved && !checked ? { _delete: true } : {}),
              }
        })
        return curr.concat(checkedPixels)
      }, [])
      .filter(pixel => pixel)

    updateField('pixels', pixels)
    closeImportFbPixelDialog()
    setTimeout(() => {
      submitForm()
    }, 100)
  }

  const fbAdAccounts_element = _map(fbAdAccounts, (fbAdAccount, fbAdAccountIndex) => {
    const fbPixels_element_server = _map(fbAdAccount.pixels, (fbPixel, fbPixelIndex) => (
      <div
        key={fbPixelIndex}
        style={{ backgroundColor: fbPixel.checked ? '#363E48' : 'transparent' }}
        className="list-fb-accounts"
      >
        <div className="container-checkbox-wrapper">
          <label className="container-checkbox">
            <input
              data-testid="checked-input"
              type="checkbox"
              defaultChecked={fbPixel.checked}
              onChange={event => {
                const { checked } = event.target
                setFbAdAccounts(currValue => {
                  const newFbAdAccounts = [...currValue]
                  newFbAdAccounts[fbAdAccountIndex].pixels[fbPixelIndex].checked = checked

                  return newFbAdAccounts
                })
              }}
            />
            <span className="checkmark" />
          </label>
        </div>
        <div className="fb-pixel-name">{fbPixel.name}</div>
        <div className="id-wrapper">
          <div className="id-title">Pixel ID:</div>
          <div className="id-number">{fbPixel.id}</div>
        </div>
        <div className="token-box">
          <div className="secret-token">
            Secret Access Token
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="fbAccountsInfoToken">
                  This is a long string that identifies a user, app, or page, and can be used by the app to
                  make calls to the API. This provides temporary and secure access to the Facebook APIs. You
                  can get more information{' '}
                  <a
                    style={{ color: '#0645AD' }}
                    href="https://developers.facebook.com/docs/facebook-login/access-tokens"
                    target="_blank"
                    rel="noreferrer"
                  >
                    here
                  </a>
                </Tooltip>
              }
              trigger={['click']}
            >
              <i className="fa fa-question-circle" style={{ marginLeft: 5, cursor: 'pointer' }} />
            </OverlayTrigger>
          </div>
          <textarea
            data-testid="textarea-fb-accounts"
            className="textarea-fb-accounts"
            value={fbPixel.accessToken}
            onChange={event => {
              const { value } = event.target
              setFbAdAccounts(currValue => {
                const newFbAdAccounts = [...currValue]
                newFbAdAccounts[fbAdAccountIndex].pixels[fbPixelIndex].accessToken = value

                return newFbAdAccounts
              })
            }}
          />
        </div>
      </div>
    ))
    return (
      <div key={fbAdAccountIndex} className="table" style={{ marginTop: '20px' }}>
        <div className="fb-ad-account-title">
          {fbAdAccount.name}
          &nbsp;&#91;{fbAdAccount.currency}&#93;
          {fbAdAccount.owner && <span>&nbsp;owned by {fbAdAccount.owner.name}</span>}
          {fbAdAccount.pixelsLoading && <i className="fa fa-spin fa-circle-o-notch" aria-hidden="true" />}
          {fbAdAccount.pixelsLoadingError && (
            <div className="fbpixels-loading-error text-danger">
              Loading Pixels failed: {fbAdAccount.pixelsLoadingError}
            </div>
          )}
          {fbAdAccount.pixelCreatingError && (
            <div className="fbpixel-creating-error text-danger">
              Creating Pixel failed: {fbAdAccount.pixelCreatingError}
            </div>
          )}
        </div>
        <div className="text-right">
          {fbAdAccount.pixels.length === 0 && (
            <Button
              key={fbAdAccountIndex}
              className="btn btn-blue"
              disabled={fbAdAccount.pixelCreating}
              onClick={() => createFbPixel(fbAdAccount, fbAdAccountIndex)}
            >
              Create New Pixel
              {fbAdAccount.pixelCreating && <i className="fa fa-spin fa-circle-o-notch" aria-hidden="true" />}
            </Button>
          )}
        </div>
        {fbPixels_element_server}
      </div>
    )
  })

  const availablePixels = _some(fbAdAccounts, item => Boolean(item?.pixels?.length))

  const disableImportButton = fbAdAccountsLoading || fbAdAccountsError || !availablePixels
  return (
    <>
      <div className="row">
        <div className="col-xs-12">
          <span className="junior_text">Conversion Tracking JavaScript Code</span>
          <button
            className="btn btn-primary"
            style={{ float: 'right' }}
            type="button"
            onClick={clickImportFbPixelDialog}
          >
            <i className="fa fa-facebook-official" aria-hidden="true" /> Import Meta Pixel
          </button>
        </div>
      </div>
      {Boolean(values.pixels.length) && (
        <div className="row">
          <div className="col-xs-12" style={{ margin: '20px 0' }}>
            <SortableTable
              data={values.pixels}
              tableColumns={getTableColumns([
                { key: 'platform', label: 'Platform', normalizer: _capitalize },
                { key: 'pixelId', label: 'Pixel ID' },
                { key: 'accessToken', label: 'Access Token', normalizer: value => value || 'N/A' },
              ])}
              enableSort={true}
            />
          </div>
        </div>
      )}
      <Modal
        className="modal-dialog modal-trans"
        style={modalStyle}
        isOpen={showImportFbPixelDialog}
        contentLabel="Modal"
        onRequestClose={closeImportFbPixelDialog}
        closeTimeoutMS={150}
        ariaHideApp={false}
      >
        <div className="modal-dialog">
          <div className="modal-content" style={{ backgroundColor: '#2A2F37' }}>
            <div>
              <div className="modal-header">
                <p className="h4 text-compact">
                  Facebook Pixels
                  {fbAdAccountsLoading && <i className="fa fa-spin fa-circle-o-notch" aria-hidden="true" />}
                </p>
              </div>
              <div className="modal-body" style={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}>
                {Boolean(fbAdAccountsError) && (
                  <div className="text-danger">Loading Ad Accounts failed: {fbAdAccountsError}</div>
                )}
                {fbAdAccounts_element}
              </div>
              <div className="modal-footer no-border">
                <div className="btn-toolbar btn-toolbar-right">
                  <Button
                    className="btn btn-cancel btn-shadow"
                    type="button"
                    onClick={closeImportFbPixelDialog}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="btn btn-default btn-shadow"
                    style={{ backgroundColor: '#139679' }}
                    disabled={disableImportButton}
                    type="button"
                    onClick={importFacebookPixels}
                  >
                    Import
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}

export default FaceBookPixels
